import moment from 'moment';
import { Contributor } from './goals.types';

export const propsConfig = {
  dateNavBtnProps: {
    fontWeight: 'normal',
  },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      fontWeight: 'normal',
      _hover: {
        background: 'primary',
        fontWeight: 'normal',
        color: 'white',
      },
    },
    isInRangeBtnProps: {
      color: 'primary',
    },
    selectedBtnProps: {
      background: 'primary',
      fontWeight: 'normal',
      color: 'white',
    },
  },
  inputProps: {
    size: 'md',
    width: '100%',
  },
  popoverCompProps: {
    popoverContentProps: {
      background: 'white',
      boxShadow: 'lg',
      border: 'none',
    },
  },
};

export const customSelectStyles = {
  container: (provided: any) => ({
    ...provided,
    width: '200px',
  }),
  control: (provided: any) => ({
    ...provided,
    borderColor: provided.borderColor,
    boxShadow: 'none',
    borderRadius: '4px',
    minHeight: '40px',
    '&:hover': {
      borderColor: 'gray.300',
    },
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    backgroundColor: 'transparent',
    padding: '8px',
  }),
};

export const configs = {
  dayNames: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
};

export const getFormattedDate = (date: Date) => {
  const formattedDate =
    date.toLocaleDateString('en-US', { year: 'numeric' }) +
    '-' +
    date.toLocaleDateString('en-US', { month: '2-digit' }) +
    '-' +
    date.toLocaleDateString('en-US', { day: '2-digit' });
  return formattedDate;
};

export const parseDate = (
  dateString: string,
  format = 'DD/MM/YYYY'
): Date | undefined => {
  const momentDate = moment(dateString, format);
  return momentDate.isValid() ? momentDate.toDate() : undefined;
};

export const scopeOptions = [
  { value: 'CONTRIBUTOR', label: 'Contributor' },
  { value: 'TEAM', label: 'Team' },
  { value: 'ORG', label: 'Organization' },
];

export const timeframeOptions = [
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'WEEKLY', label: 'Weekly' },
];

export const metricOptions = [
  { value: 'count', label: 'Count' },
  { value: 'completionRate', label: 'Completion Rate' },
  { value: 'cycleTime', label: 'Cycle Time' },
  { value: 'deploymentFrequency', label: 'Deployment Frequency' },
  { value: 'bugs', label: 'Bugs' },
];

export const operatorOptions = [
  { value: 'GREATER_THAN', label: 'More than' },
  { value: 'LESS_THAN', label: 'Less than' },
  { value: 'BETWEEN', label: 'Between' },
];

export const generateId = (prefix: string) => {
  return `${prefix}_${Math.random().toString(36).substring(2, 9)}`;
};

interface Team {
  name: string;
  subTeams?: Team[];
}

export type { TeamHierarchyOption as TeamOption } from '@devd-client/devd/components';
export { flattenTeamsWithMetadata as formatTeamOptions } from '@devd-client/devd/components';

export interface MemberOption {
  value: string;
  label: string;
  email: string;
}

export const progressBarColors = {
  atRisk: '#E65100',
  behind: '#FFAD00',
  onTrack: '#1DA44A',
};

export type SegmentProps = {
  label: string;
  value: number;
  color: string;
  percentage: number;
  isFirst?: boolean;
  isLast?: boolean;
};

export type LegendItemProps = {
  label: string;
  value: number;
  total: number;
  color: string;
  hasBorder?: boolean;
};

export const getTeamNames = (teams: Team[], prefix = ''): string[] => {
  if (!teams) {
    return [];
  }
  const names: string[] = [];

  for (const team of teams) {
    names.push(prefix + team.name);
    if (team.subTeams && team.subTeams.length > 0) {
      const subNames = getTeamNames(team.subTeams);
      names.push(...subNames);
    }
  }

  return names;
};

export const getTeamParentMap = (teams: Team[]) => {
  if (!teams) {
    return {};
  }
  const teamMap: Record<string, string | null> = {};

  const traverse = (team: Team, parent: string | null = null) => {
    teamMap[team.name] = parent;
    if (team.subTeams && team.subTeams.length > 0) {
      team.subTeams.forEach((sub) => traverse(sub, team.name));
    }
  };

  teams.forEach((team) => traverse(team));
  return teamMap;
};
export const formatGoalStatus = (value: string): string => {
  if (!value) return '';

  const normalizedValue = value?.toString()?.trim();

  const words = normalizedValue?.split(/[_\s]+/);

  return words
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    ?.join(' ');
};

export const filterOptions = [
  { value: 'TEAM', label: 'Team' },
  { value: 'CONTRIBUTOR', label: 'Contributor' },
];

export const getRequiredDays = (timeframe: string) => {
  switch (timeframe) {
    case 'QUARTERLY':
      return 180;
    case 'MONTHLY':
      return 90;
    case 'WEEKLY':
      return 30;
    default:
      return 0;
  }
};

export const GROUP_LEVEL_METRICS = [
  'TOP_REVIEWER_DISTRIBUTION',
  'CONTRIBUTOR_COUNT',
  'DEPLOYMENT_FREQUENCY',
  'MEAN_TIME_TO_RECOVERY',
  'CHANGE_FAILURE_RATE',
  'SPRINT_SCOPE_CHANGE',
  'DOCUMENT_UPDATED',
  'CHANGE_FAILURE',
];

export const getContributorEmails = (contributors: Contributor[]): string[] => {
  return (
    contributors
      ?.filter((contributor) => contributor?.email)
      ?.map((contributor) => contributor?.email) || []
  );
};
